/**
 * ProfileCompletionBanner Component
 * 
 * Warning banner to remind users to complete their profile.
 * 
 * Features:
 * - Yellow warning banner style
 * - Call-to-action button
 * - Dismissible but persistent (until profile complete)
 * - Shows only when profile_completed=false
 * - Responsive design
 * 
 * Created: 2025-01-17 (Phase 4.8: User Profile Management)
 * Phase: Phase 4.8 - Frontend Implementation
 */

import React from 'react';

const ProfileCompletionBanner = ({ show, onComplete }) => {
  if (!show) return null;

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow-sm p-4 mb-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start flex-1">
          {/* Warning Icon */}
          <div className="flex-shrink-0">
            <svg 
              className="w-6 h-6 text-yellow-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
          </div>

          {/* Content */}
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-semibold text-yellow-800">
              Complete Your Profile
            </h3>
            <p className="text-sm text-yellow-700 mt-1">
              Your profile is incomplete. Please fill in all required fields to ensure full access to features and personalized tax strategies.
            </p>
            
            {/* Mobile Button */}
            <div className="mt-3 sm:hidden">
              <button
                onClick={onComplete}
                className="w-full px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 transition shadow-sm"
              >
                Complete Now →
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Button */}
        <div className="ml-4 flex-shrink-0 hidden sm:block">
          <button
            onClick={onComplete}
            className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 transition shadow-sm"
          >
            Complete Now →
          </button>
        </div>
      </div>

      {/* Progress Indicator (Optional - shows missing fields count) */}
      <div className="mt-3 ml-9">
        <div className="flex items-center text-xs text-yellow-700">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            Required fields: First Name, Last Name, Phone, Date of Birth, Address, City, State, ZIP Code
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionBanner;
